import React, {FC} from 'react'
import styles from './List.scss'
import {Link, RouteComponentProps, useHistory, useLocation} from 'react-router-dom'
import queryString from 'query-string'
import {SYSTEM_ANNOUNCES, SearchQuery, QueryResult, SystemAnnounce} from './query'
import classnames from 'classnames'
import {Tab} from 'components/ui/Tab'
import {Spacer} from 'jbc-front/components/presenters/Spacer'
import {useQuery} from 'hooks/graphql'
import {ListGroup} from 'jbc-front/components/presenters/ui/ListGroup'
import {SearchForm} from './parts/SearchForm'
import {dayOrTimeFormat} from 'libs/formatter'
import {PaginatorWithResult} from 'jbc-front/components/SearchForm'
import {useDisplaySystemAnnounceLimit} from './parts/utils'
import {Labels, labels} from './parts/schema'

export const List: FC<RouteComponentProps> = () => {
  const location = useLocation()
  const history = useHistory()
  const {search} = location
  const parsedQuery = queryString.parse(search)
  const [savedLimit, setLimit] = useDisplaySystemAnnounceLimit()

  const page = parseInt(parsedQuery['page'] as string, 10) || 1
  const per = parsedQuery['per'] ? parseInt(parsedQuery['per'] as string, 10) : parseInt(savedLimit, 10)
  const q = (parsedQuery['q'] as string) ?? ''
  const rawLabel = parsedQuery['label'] as string | undefined
  const label: Labels | undefined =
    rawLabel && labels.some((item) => item.id === rawLabel) ? (rawLabel as Labels) : undefined

  const searchQuery = {
    page: page,
    per: per,
    q: q,
    label: label,
  }

  const {data: dataImportant, loading: loadingImportant} = useQuery<QueryResult>(SYSTEM_ANNOUNCES, {
    variables: {
      isImportant: true,
      per: 200,
    },
    fetchPolicy: 'network-only',
  })
  const totalCountImportant = !loadingImportant && dataImportant ? dataImportant.systemAnnounces.totalCount : 0

  const {data: dataAll, loading: loadingAll} = useQuery<QueryResult>(SYSTEM_ANNOUNCES, {
    variables: searchQuery,
    fetchPolicy: 'network-only',
  })
  const totalCountAll = !loadingAll && dataAll ? dataAll.systemAnnounces.totalCount : 0

  return (
    <>
      <MainTab />
      <Spacer direction="y" size={30} />
      <div className="l-wrap-m">
        {totalCountImportant > 0 && (
          <>
            <ListGroup>
              <ListGroup.Title>重要なお知らせ</ListGroup.Title>
              {dataImportant?.systemAnnounces.list.map((systemAnnounce) => (
                <SystemAnnounceItem key={systemAnnounce.id} systemAnnounce={systemAnnounce} />
              ))}
            </ListGroup>
            <Spacer direction="y" size={60} />
          </>
        )}
        <SearchForm
          values={searchQuery}
          onSubmit={(values) => {
            const newSearchQuery: SearchQuery = {
              page: 1,
              per: values.per,
              q: values.q || '',
              ...(values.label && {label: values.label as Labels}),
            }
            return history.push({search: queryString.stringify(newSearchQuery, {arrayFormat: 'bracket'})})
          }}
          onClear={() => {
            const clearedQuery: SearchQuery = {
              page: 1,
              per: per,
              q: '',
            }
            history.push({search: queryString.stringify(clearedQuery, {arrayFormat: 'bracket'})})
          }}
          hasConditions={(form) => {
            return form.q !== ''
          }}
          onChangeLimit={(newLimit) => {
            setLimit(newLimit)
          }}
        />
        <ListGroup>
          <ListGroup.Title>すべてのお知らせ</ListGroup.Title>
          {dataAll?.systemAnnounces.list.map((systemAnnounce) => (
            <SystemAnnounceItem key={systemAnnounce.id} systemAnnounce={systemAnnounce} />
          ))}
        </ListGroup>
        <PaginatorWithResult
          onClick={(newPage) => {
            const newSearchQuery: SearchQuery = {...searchQuery, page: newPage}
            history.push({search: queryString.stringify(newSearchQuery, {arrayFormat: 'bracket'})})
          }}
          limit={per}
          count={totalCountAll}
          page={page}
          isLoading={loadingAll}
          failureMessage="お知らせが見つかりませんでした"
        />
      </div>
    </>
  )
}

const MainTab: FC = () => {
  return (
    <div className={classnames(styles.tabsWrap, 'u-pb0')}>
      <div className={classnames(styles.tabs, 'l-wrap-xs')}>
        <Tab active>お知らせ一覧</Tab>
        <Tab to="/notifications">通知一覧</Tab>
        <Tab to="/alerts">アラート一覧</Tab>
      </div>
    </div>
  )
}

const DATE_WITH_YEAR_LENGTH = 9
interface SystemAnnounceItemProps {
  systemAnnounce: SystemAnnounce
}

const SystemAnnounceItem: FC<SystemAnnounceItemProps> = ({systemAnnounce}) => {
  const dateOrTime = dayOrTimeFormat(String(systemAnnounce.publishAt))

  const commonProps = {
    className: classnames(styles.item, styles.list),
    children: (
      <>
        <span className={classnames(styles.date, dateOrTime.length >= DATE_WITH_YEAR_LENGTH && styles.dateWithYear)}>
          {dateOrTime}
        </span>
        {systemAnnounce.label && <div className={styles.label}>{systemAnnounce.labelName}</div>}
        <span className={styles.title}>{systemAnnounce.title}</span>
      </>
    ),
  }

  return systemAnnounce.systemAnnounceType === 'link' ? (
    <ListGroup.Item {...commonProps} as="a" href={systemAnnounce.link} target="_blank" />
  ) : (
    <ListGroup.Item {...commonProps} as={Link} to={`system_announces/${systemAnnounce.id}`} />
  )
}
